'use client'

import { Divider, Cell, Text, Label, Image, Badge } from '@vinted/web-ui'

import { useAbTest, useTrackAbTestCallback } from '@marketplace-web/feature-flags/ab-tests-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { CatalogNavigationModel, CatalogAttribute } from '@marketplace-web/catalog/catalog-data'

import { selectCatalogEvent } from '@marketplace-web/catalog/catalog-navigation-data'

type Props = {
  categories: Array<CatalogNavigationModel>
}

const CategoriesSection = ({ categories }: Props) => {
  const translate = useTranslate('header.side_bar')
  const { track } = useTracking()
  const trackAbTest = useTrackAbTestCallback()

  const scopedHomePageNavigationABtest = useAbTest('web_scoped_homepages_navigation')
  const isScopedHomepageNavigationEnabled = scopedHomePageNavigationABtest?.variant === 'on'

  const handleTitleClick =
    ({ id }: CatalogNavigationModel) =>
    () => {
      const trakingEvent = selectCatalogEvent({
        catalogIds: [id],
        attributeId: CatalogAttribute.Navigation,
      })

      track(trakingEvent)
      trackAbTest(scopedHomePageNavigationABtest)
    }

  // TODO: this function is temporary and will be removed at the end of this test
  const getUrl = (code: string, url: string, custom_url?: string | null): string => {
    let selectedUrl = url

    if (isScopedHomepageNavigationEnabled && custom_url) {
      selectedUrl = custom_url
    }

    // TODO: these hardcoded values will be removed after the abtest is over.
    if (
      !isScopedHomepageNavigationEnabled &&
      custom_url &&
      (code === 'DESIGNER_ROOT' || code === 'ELECTRONICS')
    ) {
      selectedUrl = custom_url
    }

    return selectedUrl
  }

  const renderCategoriesItem = (item: CatalogNavigationModel) => (
    <Cell
      key={item.id}
      type={Cell.Type.Navigating}
      prefix={
        <Image src={item.photo?.url} scaling={Image.Scaling.Cover} size={Image.Size.Regular} />
      }
      suffix={
        item.badge && <Badge theme={item.badge.theme || 'primary'} content={item.badge.title} />
      }
      body={
        <Text as="span" truncate>
          {item.title}
        </Text>
      }
      url={getUrl(item.code, item.url, item.customUrl)}
      urlProps={{ title: item.title }}
      onClick={handleTitleClick(item)}
      testId={`sidebar-category-item-${item.id}`}
      aria={{ 'aria-label': item.title }}
    />
  )

  return (
    <div>
      <Label styling={Label.Styling.Wide} text={translate('categories')} />
      <SeparatedList separator={<Divider />}>{categories.map(renderCategoriesItem)}</SeparatedList>
    </div>
  )
}

export default CategoriesSection
